import React, { useState } from "react";
import { createPortal } from "react-dom";
import api, { formatApiError } from "@/lib/api";
import { KeyRound, X, Eye, EyeOff, CheckCircle2 } from "lucide-react";

export default function ChangePasswordModal({ open, onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  if (!open) return null;

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); setError(""); setShow(false); setDone(false); };
  const close = () => { reset(); onClose(); };

  const submit = async (e) => {
    e.preventDefault(); setError("");
    const strong = next.length >= 8 && /[A-Z]/.test(next) && /\d/.test(next) && /[^A-Za-z0-9]/.test(next);
    if (!strong) { setError("New password must be at least 8 characters and include an uppercase letter, a number, and a special character."); return; }
    if (next !== confirm) { setError("New passwords don't match."); return; }
    if (next === current) { setError("New password must be different from the current one."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: current, new_password: next });
      if (data.access_token) localStorage.setItem("cp_token", data.access_token);
      if (data.refresh_token) localStorage.setItem("cp_refresh", data.refresh_token);
      setDone(true);
      setTimeout(close, 1400);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" data-testid="change-password-modal">
      <div className="absolute inset-0" style={{ background: "rgba(15,18,38,0.42)", backdropFilter: "blur(4px)" }} onClick={close} />
      <div className="relative glass w-full max-w-md p-6" style={{ borderRadius: 20 }}>
        <button onClick={close} className="absolute top-4 right-4 org-icon-btn" data-testid="change-password-close"><X size={16} /></button>
        <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-3" style={{ background: "rgba(64,96,255,0.12)" }}>
          <KeyRound size={20} style={{ color: "#4060FF" }} />
        </div>
        {done ? (
          <div data-testid="change-password-done">
            <div className="flex items-center gap-2 mb-1"><CheckCircle2 size={18} style={{ color: "#00863A" }} /><h3 className="font-display text-lg font-semibold tracking-tight">Password updated</h3></div>
            <p className="text-sm text-ink-2">Your new password is now active.</p>
          </div>
        ) : (
          <>
            <div className="kpi-label">Account security</div>
            <h3 className="font-display text-lg font-semibold tracking-tight mb-4">Change your password</h3>
            <form onSubmit={submit} className="space-y-3" data-testid="change-password-form">
              <input type={show ? "text" : "password"} required value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="Current password" className="input-glass w-full" data-testid="cp-current" autoFocus />
              <div className="relative">
                <input type={show ? "text" : "password"} required value={next} onChange={(e) => setNext(e.target.value)} placeholder="New password" className="input-glass w-full pr-11" data-testid="cp-new" />
                <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-3 hover:text-ink-1 transition-colors" data-testid="cp-toggle" aria-label={show ? "Hide" : "Show"}>
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <input type={show ? "text" : "password"} required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm new password" className="input-glass w-full" data-testid="cp-confirm" />
              <p className="text-[11px] text-ink-3">At least 8 characters, with one uppercase letter, one number, and one special character.</p>
              {error && <div className="chip chip-amber" data-testid="cp-error">{error}</div>}
              <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="cp-submit">{busy ? "Updating…" : "Update password"}</button>
            </form>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
